import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { functions } from '../../lib/firebase';
import { trackError, ERROR_CODES, createErrorMeta } from '../../lib/telemetry';

const auth = getAuth();

export type Plan =
  | 'FREE'
  | 'PRO-MONTHLY'
  | 'PRO-ANNUAL'
  | 'PRO_TRIAL'
  | 'ARCHIVE'
  | 'ARCHIVE-ONE-TIME';
export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'in_grace' | 'trialing';

interface SubscriptionState {
  plan: Plan;
  status: SubscriptionStatus;
  refreshAt: number | null; // timestamp
  archiveEndsAt: number | null; // timestamp for archive expiration
  isLoading: boolean;
  error: string | null;
  entitlementsVersion: number;
}

const initialState: SubscriptionState = {
  plan: 'FREE',
  status: 'active',
  refreshAt: null,
  archiveEndsAt: null,
  isLoading: false,
  error: null,
  entitlementsVersion: 0,
};

// Async thunk for creating checkout session
export const createCheckoutSession = createAsyncThunk(
  'subscription/createCheckoutSession',
  async ({ interval }: { interval: 'month' | 'year' }, { rejectWithValue }) => {
    try {
      const createCheckoutSessionFn = httpsCallable<
        { interval: 'month' | 'year' },
        { url: string; clientSecret?: string; sessionId?: string; promoAutoApplied: boolean }
      >(functions, 'createCheckoutSession');

      const result = await createCheckoutSessionFn({ interval });
      return result.data;
    } catch (error: any) {
      const errorMeta = createErrorMeta(
        'payments',
        ERROR_CODES.PAYMENT_CHECKOUT_CREATE_FAIL,
        'S2',
        'user-blocking',
        '24h',
        {
          retryable: true,
          providerCode: error?.code,
          userAction: 'checkout',
          extras: { interval },
        }
      );
      trackError('Failed to create checkout session', errorMeta, error);

      return rejectWithValue(error.message || 'Failed to create checkout session');
    }
  }
);

// Async thunk for opening customer portal
export const openCustomerPortal = createAsyncThunk(
  'subscription/openCustomerPortal',
  async (_, { rejectWithValue }) => {
    try {
      const createPortalSessionFn = httpsCallable<{}, { url: string }>(
        functions,
        'createPortalSession'
      );

      const result = await createPortalSessionFn();
      return result.data.url;
    } catch (error: any) {
      const errorMeta = createErrorMeta(
        'payments',
        ERROR_CODES.PAYMENT_PORTAL_CREATE_FAIL,
        'S2',
        'degraded',
        '24h',
        {
          retryable: true,
          providerCode: error?.code,
          userAction: 'manage_billing',
        }
      );
      trackError('Failed to create portal session', errorMeta, error);

      return rejectWithValue(error.message || 'Failed to open customer portal');
    }
  }
);

// Async thunk for beginning archive purchase
export const beginArchivePurchase = createAsyncThunk(
  'subscription/beginArchivePurchase',
  async (_, { rejectWithValue }) => {
    try {
      const beginArchivePurchaseFn = httpsCallable<{}, { url: string; promoAutoApplied: boolean }>(
        functions,
        'beginArchivePurchase'
      );

      const result = await beginArchivePurchaseFn();
      return result.data;
    } catch (error: any) {
      const errorMeta = createErrorMeta(
        'payments',
        ERROR_CODES.PAYMENT_ARCHIVE_BEGIN_FAIL,
        'S2',
        'user-blocking',
        '24h',
        {
          retryable: true,
          providerCode: error?.code,
          userAction: 'archive_checkout',
        }
      );
      trackError('Failed to begin archive purchase', errorMeta, error);

      return rejectWithValue(error.message || 'Failed to begin archive purchase');
    }
  }
);

// Async thunk to confirm a checkout session (Payment Links without webhooks)
export const confirmCheckout = createAsyncThunk(
  'subscription/confirmCheckout',
  async ({ sessionId }: { sessionId: string }, { rejectWithValue }) => {
    try {
      const confirmFn = httpsCallable<{ sessionId: string }, { ok: boolean }>(
        functions,
        'confirmCheckoutSession'
      );
      const result = await confirmFn({ sessionId });
      if (!result.data?.ok) throw new Error('Confirmation failed');
      return { success: true };
    } catch (error: any) {
      const errorMeta = createErrorMeta(
        'payments',
        ERROR_CODES.PAYMENT_CHECKOUT_CREATE_FAIL,
        'S2',
        'degraded',
        '24h',
        {
          retryable: true,
          providerCode: error?.code,
          userAction: 'confirm_checkout',
        }
      );
      trackError('Failed to confirm checkout session', errorMeta, error);
      return rejectWithValue(error.message || 'Failed to confirm checkout session');
    }
  }
);

// Async thunk for refreshing entitlements
export const refreshEntitlements = createAsyncThunk(
  'subscription/refreshEntitlements',
  async (_, { rejectWithValue, getState }) => {
    try {
      // Check if user is authenticated first
      if (!auth.currentUser) {
        console.warn('Cannot refresh entitlements: user not authenticated');
        return { success: false, reason: 'not_authenticated' };
      }

      const state = getState() as { subscription: SubscriptionState };
      const last = state.subscription.refreshAt || 0;
      const now = Date.now();
      if (now - last < 60_000) {
        return { success: true };
      }

      // Force token refresh to ensure we have valid auth
      // DISABLED AUTO-SIGN-OUT - Just log errors for now
      try {
        console.log('Attempting token refresh in refreshEntitlements...');
        await auth.currentUser.getIdToken(true);
        console.log('✅ Token refresh successful in refreshEntitlements');
      } catch (tokenError: any) {
        console.error('❌ Token refresh failed in refreshEntitlements:', tokenError);
        console.error('Token error details:', {
          code: tokenError.code,
          message: tokenError.message,
          name: tokenError.name,
        });

        // DISABLED: Don't auto-sign-out anymore
        // Just log the error and continue
        console.warn(
          '⚠️ Token refresh failed but continuing anyway. Will try syncSubscriptionToAuth.'
        );
      }

      const syncSubscriptionToAuthFn = httpsCallable<{}, { success: boolean }>(
        functions,
        'syncSubscriptionToAuth'
      );

      const result = await syncSubscriptionToAuthFn();

      if (!result.data.success) {
        throw new Error('Failed to sync subscription to auth');
      }

      // Force refresh of ID token to get updated claims
      try {
        await auth.currentUser.getIdToken(true);
        console.log('✅ Second token refresh successful after syncSubscriptionToAuth');
      } catch (tokenError: any) {
        console.error('❌ Second token refresh failed:', tokenError);
        console.error('Second token error details:', {
          code: tokenError.code,
          message: tokenError.message,
        });

        // DISABLED: Don't auto-sign-out anymore
        // Just log and continue - might still work
        console.warn('⚠️ Second token refresh failed but continuing anyway.');
      }

      return { success: true };
    } catch (error: any) {
      const errorMeta = createErrorMeta(
        'auth',
        ERROR_CODES.AUTH_CLAIMS_REFRESH_FAIL,
        'S2',
        'degraded',
        '24h',
        {
          retryable: true,
          providerCode: error?.code,
          userAction: 'refresh_entitlements',
        }
      );
      trackError('Failed to refresh entitlements', errorMeta, error);

      return rejectWithValue(error.message || 'Failed to refresh entitlements');
    }
  }
);

// Async thunk for canceling trial at trial end
export const cancelTrialAtEnd = createAsyncThunk(
  'subscription/cancelTrialAtEnd',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const cancelTrialFn = httpsCallable<
        { mode: 'now' | 'at_trial_end' },
        { ok: boolean; code: string; note?: string }
      >(functions, 'cancelTrial');

      const result = await cancelTrialFn({ mode: 'at_trial_end' });

      if (!result.data.ok) {
        throw new Error(`Cancellation failed: ${result.data.code}`);
      }

      // Refresh entitlements after cancellation
      await dispatch(refreshEntitlements());

      return result.data;
    } catch (error: any) {
      const errorMeta = createErrorMeta(
        'payments',
        ERROR_CODES.PAYMENT_CANCEL_FAIL,
        'S2',
        'user-blocking',
        '24h',
        {
          retryable: true,
          providerCode: error?.code,
          userAction: 'cancel_trial_at_end',
        }
      );
      trackError('Failed to cancel trial at end', errorMeta, error);

      return rejectWithValue(error.message || 'Failed to cancel trial at end');
    }
  }
);

// Async thunk for canceling trial now
export const cancelTrialNow = createAsyncThunk(
  'subscription/cancelTrialNow',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const cancelTrialFn = httpsCallable<
        { mode: 'now' | 'at_trial_end' },
        { ok: boolean; code: string; note?: string }
      >(functions, 'cancelTrial');

      const result = await cancelTrialFn({ mode: 'now' });

      if (!result.data.ok) {
        throw new Error(`Cancellation failed: ${result.data.code}`);
      }

      // Refresh entitlements after cancellation
      await dispatch(refreshEntitlements());

      return result.data;
    } catch (error: any) {
      const errorMeta = createErrorMeta(
        'payments',
        ERROR_CODES.PAYMENT_CANCEL_FAIL,
        'S2',
        'user-blocking',
        '24h',
        {
          retryable: true,
          providerCode: error?.code,
          userAction: 'cancel_trial_now',
        }
      );
      trackError('Failed to cancel trial now', errorMeta, error);

      return rejectWithValue(error.message || 'Failed to cancel trial now');
    }
  }
);

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    setEntitlements: (
      state,
      action: PayloadAction<{
        plan: Plan;
        status: SubscriptionStatus;
        refreshAt: number | null;
        archiveEndsAt: number | null;
        entitlementsVersion: number;
      }>
    ) => {
      state.plan = action.payload.plan;
      state.status = action.payload.status;
      state.refreshAt = action.payload.refreshAt;
      state.archiveEndsAt = action.payload.archiveEndsAt;
      state.entitlementsVersion = action.payload.entitlementsVersion;
      state.error = null;
    },
    resetSubscription: () => initialState,
  },
  extraReducers: builder => {
    builder
      // Load subscription from Firestore when auth initializes
      .addCase('auth/initialize/fulfilled' as any, (state, action: any) => {
        const userData = action.payload;
        if (userData?.billing) {
          const billing = userData.billing as any;
          if (billing.plan) {
            state.plan = billing.plan as Plan;
            state.status = billing.status || 'active';
            state.archiveEndsAt =
              billing.archiveEndsAt?.toMillis?.() || billing.archiveEndsAt || null;
            state.refreshAt = Date.now();
            state.entitlementsVersion = Date.now();
            console.log('[SubscriptionSlice] Loaded subscription from Firestore:', {
              plan: billing.plan,
              status: billing.status,
              archiveEndsAt: state.archiveEndsAt,
            });
          }
        } else {
          console.log(
            '[SubscriptionSlice] No billing data in user document, plan remains:',
            state.plan
          );
        }
      })
      // Create checkout session
      .addCase(createCheckoutSession.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createCheckoutSession.fulfilled, (state, _action) => {
        state.isLoading = false;
        // Note: The URL is handled by the component that dispatches this action
        state.error = null;
      })
      .addCase(createCheckoutSession.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Open customer portal
      .addCase(openCustomerPortal.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(openCustomerPortal.fulfilled, (state, _action) => {
        state.isLoading = false;
        // Note: The URL is handled by the component that dispatches this action
        state.error = null;
      })
      .addCase(openCustomerPortal.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Refresh entitlements
      .addCase(refreshEntitlements.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(refreshEntitlements.fulfilled, (state, _action) => {
        state.isLoading = false;
        state.entitlementsVersion += 1; // Increment version to trigger re-evaluation
        state.refreshAt = Date.now();
        state.error = null;
      })
      .addCase(refreshEntitlements.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Begin archive purchase
      .addCase(beginArchivePurchase.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(beginArchivePurchase.fulfilled, (state, _action) => {
        state.isLoading = false;
        // Note: The URL is handled by the component that dispatches this action
        state.error = null;
      })
      .addCase(beginArchivePurchase.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Cancel trial at end
      .addCase(cancelTrialAtEnd.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelTrialAtEnd.fulfilled, (state, _action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(cancelTrialAtEnd.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Cancel trial now
      .addCase(cancelTrialNow.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelTrialNow.fulfilled, (state, _action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(cancelTrialNow.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setEntitlements, resetSubscription } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
